import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardList, Plus, ChevronLeft, Search, Check, X,
  CheckCircle2, AlertCircle, Clock, Trash2, Calendar,
  Building2, Hash, Tag, Layers, ChevronDown, Filter,
  RefreshCw, Play, CalendarClock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import { SidePanel } from '../components/SidePanel';

// ─── Constantes ───────────────────────────────────────────────────────────────
const STATUTS_MAT = [
  { id: 'disponible', label: 'Disponible' },
  { id: 'loue', label: 'Loué' },
  { id: 'en_reparation', label: 'En réparation' },
  { id: 'vendu', label: 'Vendu' },
  { id: 'hors_service', label: 'Hors service' },
];
const PARCS_MAT = [
  { id: 'location_courte_duree', label: 'Location C.D.' },
  { id: 'client', label: 'Client' },
  { id: 'stock', label: 'Stock' },
  { id: 'vehicule_de_societe', label: 'Véhicule société' },
  { id: 'epave', label: 'Épave' },
];
const STATUTS_COMPTAGE_OPT = [
  { id: 'present', label: 'Présent' },
  { id: 'introuvable', label: 'Introuvable' },
  { id: 'jamais', label: 'Jamais compté' },
];
const STATUT_CFG = {
  present:    { label: 'Présent',     color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC' },
  introuvable:{ label: 'Introuvable', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  en_attente: { label: 'En attente',  color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
};
const FREQUENCES = [
  { id: 'hebdomadaire', label: 'Hebdomadaire' },
  { id: 'mensuel',      label: 'Mensuel' },
  { id: 'trimestriel',  label: 'Trimestriel' },
  { id: 'semestriel',   label: 'Semestriel' },
  { id: 'annuel',       label: 'Annuel' },
];
const FILTER_DEFS = [
  { key: 'familles',                 label: 'Famille',         icon: Layers,        type: 'multi' },
  { key: 'sous_famille_ids',         label: 'Sous-famille',    icon: Layers,        type: 'multi' },
  { key: 'parcs',                    label: 'Parc',            icon: Tag,           type: 'multi' },
  { key: 'dernier_comptage_statuts', label: 'Statut comptage', icon: ClipboardList, type: 'multi' },
  { key: 'statuts',                  label: 'Statut',          icon: Tag,           type: 'multi' },
  { key: 'proprietaire_ids',         label: 'Propriétaire',    icon: Building2,     type: 'multi' },
  { key: 'exploitant_ids',           label: 'Exploitant',      icon: Building2,     type: 'multi' },
  { key: 'marque_ids',               label: 'Marque',          icon: Tag,           type: 'multi' },
  { key: 'modele_ids',               label: 'Modèle',          icon: Tag,           type: 'multi' },
  { key: 'numero_parc',              label: 'N° Parc',         icon: Hash,          type: 'text'  },
  { key: 'numero_serie',             label: 'N° Série',        icon: Hash,          type: 'text'  },
];

const EMPTY_SEL = {
  familles: [], sous_famille_ids: [], parcs: [], dernier_comptage_statuts: [],
  statuts: [], proprietaire_ids: [], exploitant_ids: [], marque_ids: [], modele_ids: [],
  numero_parc: null, numero_serie: null,
};

const ANCIENNETE_OPTS = [
  { id: null, label: 'Tous' },
  { id: 1,    label: '> 1 mois' },
  { id: 3,    label: '> 3 mois' },
  { id: 6,    label: '> 6 mois' },
  { id: 12,   label: '> 12 mois' },
];

// ─── Composants utilitaires ───────────────────────────────────────────────────
function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.en_attente;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {statut === 'present'     && <CheckCircle2 size={11} />}
      {statut === 'introuvable' && <AlertCircle  size={11} />}
      {statut === 'en_attente'  && <Clock        size={11} />}
      {cfg.label}
    </span>
  );
}

function ProgressBar({ present, introuvable, total }) {
  const pctP = total > 0 ? (present / total) * 100 : 0;
  const pctI = total > 0 ? (introuvable / total) * 100 : 0;
  return (
    <div className="w-full h-2 rounded-full overflow-hidden flex" style={{ background: '#E5E7EB' }}>
      <div style={{ width: `${pctP}%`, background: '#16A34A', transition: 'width 0.3s' }} />
      <div style={{ width: `${pctI}%`, background: '#DC2626', transition: 'width 0.3s' }} />
    </div>
  );
}

function FreqBadge({ frequence }) {
  const cfg = {
    hebdomadaire: { label: 'Hebdo',   color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
    mensuel:      { label: 'Mensuel', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
    trimestriel:  { label: 'Trimestr.', color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD' },
    semestriel:   { label: 'Semestr.', color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
    annuel:       { label: 'Annuel',  color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
  }[frequence] || { label: frequence, color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <RefreshCw size={10} />{cfg.label}
    </span>
  );
}

function FilterChips({ filtres }) {
  if (!filtres) return null;
  const chips = [];
  const push = (values, noms) => {
    if (!values?.length) return;
    const labels = noms?.length ? noms : values;
    return labels.slice(0, 2).join(', ') + (labels.length > 2 ? ` +${labels.length - 2}` : '');
  };
  if (filtres.familles?.length)        chips.push({ k: 'Famille',   v: push(filtres.familles, filtres.familles) });
  if (filtres.sous_famille_ids?.length) chips.push({ k: 'S-famille', v: push(filtres.sous_famille_ids, filtres.sous_famille_noms) });
  if (filtres.parcs?.length)            chips.push({ k: 'Parc',      v: push(filtres.parcs, filtres.parcs?.map(p => PARCS_MAT.find(x => x.id === p)?.label || p)) });
  if (filtres.dernier_comptage_statuts?.length) chips.push({ k: 'Cptg', v: push(filtres.dernier_comptage_statuts, filtres.dernier_comptage_statuts?.map(s => STATUTS_COMPTAGE_OPT.find(x => x.id === s)?.label || s)) });
  if (filtres.statuts?.length)          chips.push({ k: 'Statut',   v: push(filtres.statuts, filtres.statuts?.map(s => STATUTS_MAT.find(x => x.id === s)?.label || s)) });
  if (filtres.proprietaire_ids?.length) chips.push({ k: 'Proprio',  v: push(filtres.proprietaire_ids, filtres.proprietaire_noms) });
  if (filtres.exploitant_ids?.length)   chips.push({ k: 'Exploit',  v: push(filtres.exploitant_ids, filtres.exploitant_noms) });
  if (filtres.marque_ids?.length)       chips.push({ k: 'Marque',   v: push(filtres.marque_ids, filtres.marque_noms) });
  if (filtres.modele_ids?.length)       chips.push({ k: 'Modèle',   v: push(filtres.modele_ids, filtres.modele_noms) });
  if (filtres.numero_parc)              chips.push({ k: 'N°Parc',   v: filtres.numero_parc });
  if (filtres.numero_serie)             chips.push({ k: 'N°Série',  v: filtres.numero_serie });
  if (filtres.dernier_comptage_anciennete) chips.push({ k: 'Cptg.', v: `> ${filtres.dernier_comptage_anciennete} mois` });
  if (filtres.proprietaire_nom && !filtres.proprietaire_ids?.length) chips.push({ k: 'Proprio', v: filtres.proprietaire_nom });
  if (filtres.exploitant_nom && !filtres.exploitant_ids?.length)     chips.push({ k: 'Exploit', v: filtres.exploitant_nom });
  if (filtres.categorie && !filtres.familles?.length) chips.push({ k: 'Famille', v: filtres.categorie });
  if (!chips.length) return <span className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Tous les matériels</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.filter(c => c.v).map(c => (
        <span key={c.k} className="text-xs px-2 py-0.5 rounded-full"
          style={{ background: '#F1F5F9', color: '#475569' }}>
          {c.k}: {c.v}
        </span>
      ))}
    </div>
  );
}

// ─── Multi-Select Filter ──────────────────────────────────────────────────────
function MultiSelectFilter({ filterKey, label, icon: Icon, options, selected, onChange, onSearch }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Recherche serveur (si onSearch fourni)
  useEffect(() => {
    if (!onSearch) return;
    if (search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await onSearch(search); setSearchResults(r); } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, onSearch]);

  const filtered = onSearch
    ? (search.length < 2 ? [] : searchResults)
    : options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
  const isSelected = (id) => selected.some(s => s.id === id);
  const count = selected.length;
  const toggle = (opt) => onChange(isSelected(opt.id) ? selected.filter(s => s.id !== opt.id) : [...selected, opt]);
  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`filter-toggle-${filterKey}`}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap"
        style={{ background: count > 0 ? 'var(--primary-solid)' : 'white', color: count > 0 ? 'white' : 'var(--text-secondary)', borderColor: count > 0 ? 'var(--primary-solid)' : 'var(--border)' }}>
        <Icon size={11} />{label}
        {count > 0 && <span className="flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold" style={{ background: 'rgba(255,255,255,0.3)' }}>{count}</span>}
        <ChevronDown size={10} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white rounded-xl border shadow-2xl" style={{ borderColor: 'var(--border)', minWidth: '220px', maxWidth: '280px' }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-light)' }}>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={onSearch ? "Tapez 2 car. pour rechercher..." : "Rechercher..."}
                className="w-full pl-6 pr-2 py-1.5 text-xs rounded-lg border outline-none" style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }} />
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
            {filtered.length === 0
              ? <div className="px-3 py-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  {onSearch && search.length < 2 ? 'Tapez au moins 2 caractères' : 'Aucun résultat'}
                </div>
              : filtered.map(opt => (
                <label key={opt.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={isSelected(opt.id)} onChange={() => toggle(opt)}
                    className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ accentColor: 'var(--primary-solid)' }} />
                  <span className="text-sm truncate" style={{ color: 'var(--text)' }}>{opt.label}</span>
                  {isSelected(opt.id) && <Check size={12} className="ml-auto flex-shrink-0" style={{ color: 'var(--primary-solid)' }} />}
                </label>
              ))}
          </div>
          {selected.length > 0 && (
            <div className="px-3 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{selected.length} sélectionné{selected.length > 1 ? 's' : ''}</span>
              <button type="button" onClick={() => onChange([])} className="text-xs font-medium hover:underline" style={{ color: '#DC2626' }}>Effacer</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TextFilter({ filterKey, label, icon: Icon, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const ref = useRef(null);
  const isActive = !!value;
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const apply = () => { onChange(draft.trim() || null); setOpen(false); };
  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`filter-toggle-${filterKey}`}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap"
        style={{ background: isActive ? 'var(--primary-solid)' : 'white', color: isActive ? 'white' : 'var(--text-secondary)', borderColor: isActive ? 'var(--primary-solid)' : 'var(--border)' }}>
        <Icon size={11} />{label}
        {isActive && <span className="opacity-80 max-w-[60px] truncate text-xs">: {value}</span>}
        <ChevronDown size={10} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white rounded-xl border shadow-2xl p-3" style={{ borderColor: 'var(--border)', minWidth: '220px' }}>
          <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Contient :</label>
          <div className="flex gap-2">
            <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') setOpen(false); }}
              placeholder="Texte à rechercher..." className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border outline-none"
              style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }} />
            <button type="button" onClick={apply} className="px-3 py-1.5 text-xs rounded-lg font-semibold text-white" style={{ background: 'var(--primary-solid)' }}>OK</button>
          </div>
          {value && (
            <button type="button" onClick={() => { onChange(null); setDraft(''); setOpen(false); }}
              className="mt-2 text-xs w-full text-center hover:underline" style={{ color: '#DC2626' }}>Effacer</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Panneau création/édition d'un Plan ───────────────────────────────────────
function PlanPanel({ plan, open, onClose, onSaved }) {
  const [nom, setNom]           = useState(plan?.nom || '');
  const [frequence, setFrequence] = useState(plan?.frequence || 'trimestriel');
  const [dateProchaine, setDateProchaine] = useState(plan?.date_prochaine || '');
  const [saving, setSaving]     = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [anciennete, setAnciennete] = useState(plan?.filtres?.dernier_comptage_anciennete ?? null);

  const [familles, setFamilles]         = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques]           = useState([]);
  const [modeles, setModeles]           = useState([]);

  const [sel, setSel] = useState(plan ? {
    familles:                 (plan.filtres?.familles || []).map(v => ({ id: v, label: v })),
    sous_famille_ids:         (plan.filtres?.sous_famille_ids || []).map((id, i) => ({ id, label: plan.filtres?.sous_famille_noms?.[i] || id })),
    parcs:                    (plan.filtres?.parcs || []).map(v => ({ id: v, label: PARCS_MAT.find(x => x.id === v)?.label || v })),
    dernier_comptage_statuts: (plan.filtres?.dernier_comptage_statuts || []).map(v => ({ id: v, label: STATUTS_COMPTAGE_OPT.find(x => x.id === v)?.label || v })),
    statuts:                  (plan.filtres?.statuts || []).map(v => ({ id: v, label: STATUTS_MAT.find(x => x.id === v)?.label || v })),
    proprietaire_ids:         (plan.filtres?.proprietaire_ids || []).map((id, i) => ({ id, label: plan.filtres?.proprietaire_noms?.[i] || id })),
    exploitant_ids:           (plan.filtres?.exploitant_ids || []).map((id, i) => ({ id, label: plan.filtres?.exploitant_noms?.[i] || id })),
    marque_ids:               (plan.filtres?.marque_ids || []).map((id, i) => ({ id, label: plan.filtres?.marque_noms?.[i] || id })),
    modele_ids:               (plan.filtres?.modele_ids || []).map((id, i) => ({ id, label: plan.filtres?.modele_noms?.[i] || id })),
    numero_parc:  plan.filtres?.numero_parc || null,
    numero_serie: plan.filtres?.numero_serie || null,
  } : { ...EMPTY_SEL });

  useEffect(() => {
    api.getFamilles?.().then(r => setFamilles((r.data || []).map(f => ({ id: f.nom || f.famille_id, label: f.nom })))).catch(() => {});
    api.getSousFamilles?.().then(r => setSousFamilles((r.data || []).map(sf => ({ id: sf.sous_famille_id, label: sf.nom })))).catch(() => {});
    api.getMarques?.().then(r => setMarques((r.data || []).map(m => ({ id: m.marque_id, label: m.nom })))).catch(() => {});
    api.getModeles?.().then(r => setModeles((r.data || []).map(m => ({ id: m.modele_id, label: m.nom })))).catch(() => {});
  }, []);

  const buildFiltres = () => {
    const f = {};
    if (sel.familles.length)                 { f.familles = sel.familles.map(s => s.id); }
    if (sel.sous_famille_ids.length)         { f.sous_famille_ids = sel.sous_famille_ids.map(s => s.id); f.sous_famille_noms = sel.sous_famille_ids.map(s => s.label); }
    if (sel.parcs.length)                    { f.parcs = sel.parcs.map(s => s.id); }
    if (sel.dernier_comptage_statuts.length) { f.dernier_comptage_statuts = sel.dernier_comptage_statuts.map(s => s.id); }
    if (sel.statuts.length)                  { f.statuts = sel.statuts.map(s => s.id); }
    if (sel.proprietaire_ids.length)         { f.proprietaire_ids = sel.proprietaire_ids.map(s => s.id); f.proprietaire_noms = sel.proprietaire_ids.map(s => s.label); }
    if (sel.exploitant_ids.length)           { f.exploitant_ids = sel.exploitant_ids.map(s => s.id); f.exploitant_noms = sel.exploitant_ids.map(s => s.label); }
    if (sel.marque_ids.length)               { f.marque_ids = sel.marque_ids.map(s => s.id); f.marque_noms = sel.marque_ids.map(s => s.label); }
    if (sel.modele_ids.length)               { f.modele_ids = sel.modele_ids.map(s => s.id); f.modele_noms = sel.modele_ids.map(s => s.label); }
    if (sel.numero_parc)  f.numero_parc  = sel.numero_parc;
    if (sel.numero_serie) f.numero_serie = sel.numero_serie;
    if (anciennete !== null) f.dernier_comptage_anciennete = anciennete;
    return f;
  };

  const refreshPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.previewComptage(buildFiltres());
      setPreviewCount(r.data.count);
    } catch { setPreviewCount(null); }
    finally { setLoadingPreview(false); }
  };

  const activeCount = Object.values(sel).flat().filter(Boolean).length + (anciennete !== null ? 1 : 0);

  const handleSubmit = async () => {
    if (!nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        nom: nom.trim(),
        frequence,
        filtres: buildFiltres(),
        date_prochaine: dateProchaine || undefined,
      };
      let r;
      if (plan) {
        r = await api.updatePlanComptage(plan.plan_id, payload);
      } else {
        r = await api.createPlanComptage(payload);
      }
      toast.success(plan ? 'Plan mis à jour' : 'Plan créé');
      onSaved(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const getOptions = (key) => ({
    familles: familles, sous_famille_ids: sousFamilles, parcs: PARCS_MAT,
    dernier_comptage_statuts: STATUTS_COMPTAGE_OPT, statuts: STATUTS_MAT,
    proprietaire_ids: [], exploitant_ids: [],
    marque_ids: marques, modele_ids: modeles,
  })[key] || [];

  const onSearchEntreprises = async (q) => {
    try {
      const res = await api.getEntreprises({ slim: true, search: q });
      return (Array.isArray(res.data) ? res.data : []).map(e => ({ id: e.entreprise_id, label: e.nom }));
    } catch { return []; }
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={plan ? 'Modifier le plan' : 'Nouveau plan de comptage'}
      subtitle="Définissez les matériels à compter et la fréquence"
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {activeCount > 0
            ? <button type="button" onClick={() => { setSel({ ...EMPTY_SEL }); setAnciennete(null); }}
                className="text-xs font-medium hover:underline" style={{ color: 'var(--text-muted)' }}>Réinitialiser les filtres</button>
            : <div />}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium"
              style={{ color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}>Annuler</button>
            <button type="button" onClick={handleSubmit} disabled={saving || !nom.trim()} data-testid="save-plan-btn"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Enregistrement...' : (plan ? 'Mettre à jour' : 'Créer le plan')}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Nom */}
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nom du plan *</label>
          <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex : Comptage nacelles trimestriel"
            data-testid="plan-nom-input"
            className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        </div>

        {/* Fréquence + date prochaine */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Fréquence *</label>
            <select value={frequence} onChange={e => setFrequence(e.target.value)} data-testid="plan-frequence-select"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }}>
              {FREQUENCES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Prochaine échéance</label>
            <input type="date" value={dateProchaine} onChange={e => setDateProchaine(e.target.value)} data-testid="plan-date-input"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Calculée auto si vide</p>
          </div>
        </div>

        {/* Filtres */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Filtres matériels</label>
            <div className="flex items-center gap-2">
              {previewCount !== null && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                  {previewCount} matériel{previewCount > 1 ? 's' : ''}
                </span>
              )}
              <button type="button" onClick={refreshPreview} disabled={loadingPreview}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all"
                style={{ color: 'var(--text-secondary)', borderColor: 'var(--border)' }}>
                <Filter size={11} />{loadingPreview ? '...' : 'Prévisualiser'}
              </button>
            </div>
          </div>

          {/* Filtre ancienneté du dernier comptage */}
          <div className="mb-3 p-3 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid var(--border-light)' }}>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              Dernier comptage remontant à plus de...
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ANCIENNETE_OPTS.map(opt => (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() => setAnciennete(opt.id)}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
                  style={{
                    background: anciennete === opt.id ? 'var(--primary-solid)' : 'white',
                    color: anciennete === opt.id ? 'white' : 'var(--text-secondary)',
                    borderColor: anciennete === opt.id ? 'var(--primary-solid)' : 'var(--border)',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTER_DEFS.map(def => def.type === 'multi' ? (
              <MultiSelectFilter key={def.key} filterKey={def.key} label={def.label} icon={def.icon}
                options={getOptions(def.key)}
                selected={sel[def.key] || []}
                onChange={v => setSel(s => ({ ...s, [def.key]: v }))}
                onSearch={['proprietaire_ids','exploitant_ids'].includes(def.key) ? onSearchEntreprises : undefined} />
            ) : (
              <TextFilter key={def.key} filterKey={def.key} label={def.label} icon={def.icon}
                value={sel[def.key]}
                onChange={v => setSel(s => ({ ...s, [def.key]: v }))} />
            ))}
          </div>
          </div>
        </div>
      </SidePanel>
  );
}

// ─── Onglet Plans ─────────────────────────────────────────────────────────────
function PlansTab() {
  const [plans, setPlans]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showPanel, setShowPanel] = useState(false);
  const [editPlan, setEditPlan]   = useState(null);
  const [launching, setLaunching] = useState(null);
  const [deleting, setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getPlansComptage(); setPlans(r.data || []); }
    catch { toast.error('Erreur chargement des plans'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (plan) => {
    setPlans(prev => {
      const idx = prev.findIndex(p => p.plan_id === plan.plan_id);
      if (idx >= 0) { const n = [...prev]; n[idx] = plan; return n; }
      return [plan, ...prev];
    });
    setShowPanel(false);
    setEditPlan(null);
  };

  const handleLancer = async (plan) => {
    if (!window.confirm(`Lancer un comptage depuis le plan "${plan.nom}" ?`)) return;
    setLaunching(plan.plan_id);
    try {
      await api.lancerPlanComptage(plan.plan_id);
      toast.success('Session de comptage créée — consultez l\'onglet Sessions');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du lancement');
    } finally { setLaunching(null); }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Supprimer le plan "${plan.nom}" ?`)) return;
    setDeleting(plan.plan_id);
    try {
      await api.deletePlanComptage(plan.plan_id);
      setPlans(prev => prev.filter(p => p.plan_id !== plan.plan_id));
      toast.success('Plan supprimé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally { setDeleting(null); }
  };

  const isDue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) <= new Date();
  };

  return (
    <div>
      {(showPanel || editPlan) && (
        <PlanPanel
          key={editPlan?.plan_id || 'create'}
          plan={editPlan}
          open={true}
          onClose={() => { setShowPanel(false); setEditPlan(null); }}
          onSaved={handleSaved}
        />
      )}

      <div className="flex items-center justify-between mb-5">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {plans.length} plan{plans.length !== 1 ? 's' : ''} de comptage récurrent
        </p>
        <button onClick={() => setShowPanel(true)} data-testid="new-plan-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouveau plan
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border" style={{ borderColor: 'var(--border)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--primary-50)' }}>
            <CalendarClock size={28} style={{ color: 'var(--primary-solid)' }} />
          </div>
          <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)',  }}>Aucun plan de comptage</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Créez votre premier plan pour planifier des inventaires récurrents</p>
          <button onClick={() => setShowPanel(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={16} /> Créer un plan
          </button>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="plans-list">
          {plans.map(plan => {
            const due = isDue(plan.date_prochaine);
            return (
              <div key={plan.plan_id} data-testid={`plan-card-${plan.plan_id}`}
                onClick={() => setEditPlan(plan)}
                className="bg-white rounded-2xl border p-5 hover:shadow-md transition-all group cursor-pointer"
                style={{ borderColor: due ? '#FCA5A5' : 'var(--border)', boxShadow: due ? '0 0 0 2px #FEE2E2' : undefined }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-base" style={{  color: 'var(--text)' }}>{plan.nom}</h3>
                      <FreqBadge frequence={plan.frequence} />
                      {due && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                          <AlertCircle size={10} /> Échéance dépassée
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mb-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {plan.date_prochaine && (
                        <span className="flex items-center gap-1">
                          <Calendar size={11} />
                          Prochain : <strong style={{ color: due ? '#DC2626' : 'var(--text-secondary)' }}>
                            {new Date(plan.date_prochaine).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </strong>
                        </span>
                      )}
                      {plan.date_derniere && (
                        <span className="flex items-center gap-1">
                          <Check size={11} />
                          Dernier : {new Date(plan.date_derniere).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      <span>{plan.nb_sessions || 0} session{(plan.nb_sessions || 0) > 1 ? 's' : ''}</span>
                    </div>
                    <FilterChips filtres={plan.filtres} />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={e => { e.stopPropagation(); handleLancer(plan); }} disabled={launching === plan.plan_id}
                      data-testid={`lancer-plan-${plan.plan_id}`}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
                      style={{ background: due ? '#DC2626' : 'var(--primary-solid)' }}>
                      <Play size={13} fill="currentColor" />
                      {launching === plan.plan_id ? '...' : 'Lancer'}
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(plan); }} disabled={deleting === plan.plan_id}
                      data-testid={`delete-plan-${plan.plan_id}`}
                      className="p-2 rounded-xl hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100">
                      <Trash2 size={14} style={{ color: '#DC2626' }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Side-panel création de session manuelle ─────────────────────────────────
function CreationPanel({ open, onClose, onCreated }) {
  const [titre, setTitre]           = useState('');
  const [datePlanifiee, setDatePlanifiee] = useState('');
  const [saving, setSaving]         = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [anciennete, setAnciennete] = useState(null);

  const [familles, setFamilles]         = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques]           = useState([]);
  const [modeles, setModeles]           = useState([]);

  const [sel, setSel] = useState({ ...EMPTY_SEL });

  useEffect(() => {
    api.getFamilles?.().then(r => setFamilles((r.data || []).map(f => ({ id: f.nom || f.famille_id, label: f.nom })))).catch(() => {});
    api.getSousFamilles?.().then(r => setSousFamilles((r.data || []).map(sf => ({ id: sf.sous_famille_id, label: sf.nom })))).catch(() => {});
    api.getMarques?.().then(r => setMarques((r.data || []).map(m => ({ id: m.marque_id, label: m.nom })))).catch(() => {});
    api.getModeles?.().then(r => setModeles((r.data || []).map(m => ({ id: m.modele_id, label: m.nom })))).catch(() => {});
  }, []);

  const buildFiltres = () => {
    const f = {};
    if (sel.familles.length)                 { f.familles = sel.familles.map(s => s.id); }
    if (sel.sous_famille_ids.length)         { f.sous_famille_ids = sel.sous_famille_ids.map(s => s.id); f.sous_famille_noms = sel.sous_famille_ids.map(s => s.label); }
    if (sel.parcs.length)                    { f.parcs = sel.parcs.map(s => s.id); }
    if (sel.dernier_comptage_statuts.length) { f.dernier_comptage_statuts = sel.dernier_comptage_statuts.map(s => s.id); }
    if (sel.statuts.length)                  { f.statuts = sel.statuts.map(s => s.id); }
    if (sel.proprietaire_ids.length)         { f.proprietaire_ids = sel.proprietaire_ids.map(s => s.id); f.proprietaire_noms = sel.proprietaire_ids.map(s => s.label); }
    if (sel.exploitant_ids.length)           { f.exploitant_ids = sel.exploitant_ids.map(s => s.id); f.exploitant_noms = sel.exploitant_ids.map(s => s.label); }
    if (sel.marque_ids.length)               { f.marque_ids = sel.marque_ids.map(s => s.id); f.marque_noms = sel.marque_ids.map(s => s.label); }
    if (sel.modele_ids.length)               { f.modele_ids = sel.modele_ids.map(s => s.id); f.modele_noms = sel.modele_ids.map(s => s.label); }
    if (sel.numero_parc)  f.numero_parc  = sel.numero_parc;
    if (sel.numero_serie) f.numero_serie = sel.numero_serie;
    if (anciennete !== null) f.dernier_comptage_anciennete = anciennete;
    return f;
  };

  const refreshPreview = async () => {
    setLoadingPreview(true);
    try { const r = await api.previewComptage(buildFiltres()); setPreviewCount(r.data.count); }
    catch { setPreviewCount(null); }
    finally { setLoadingPreview(false); }
  };

  const handleSubmit = async () => {
    if (!titre.trim()) { toast.error('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      const r = await api.createComptage({ titre: titre.trim(), date_planifiee: datePlanifiee || undefined, filtres: buildFiltres() });
      toast.success(`Session créée — ${r.data.nb_total} matériels`);
      onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally { setSaving(false); }
  };

  const getOptions = (key) => ({
    familles: familles, sous_famille_ids: sousFamilles, parcs: PARCS_MAT,
    dernier_comptage_statuts: STATUTS_COMPTAGE_OPT, statuts: STATUTS_MAT,
    proprietaire_ids: [], exploitant_ids: [],
    marque_ids: marques, modele_ids: modeles,
  })[key] || [];

  const onSearchEntreprises = async (q) => {
    try {
      const res = await api.getEntreprises({ slim: true, search: q });
      return (Array.isArray(res.data) ? res.data : []).map(e => ({ id: e.entreprise_id, label: e.nom }));
    } catch { return []; }
  };

  const activeCount = Object.values(sel).flat().filter(Boolean).length + (anciennete !== null ? 1 : 0);

  return (
    <SidePanel
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Nouvelle session de comptage"
      subtitle="Définissez les critères de sélection des matériels"
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {activeCount > 0
            ? <button type="button" onClick={() => { setSel({ ...EMPTY_SEL }); setAnciennete(null); }}
                className="text-xs font-medium hover:underline" style={{ color: 'var(--text-muted)' }}>Réinitialiser</button>
            : <div />}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium"
              style={{ color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}>Annuler</button>
            <button type="button" onClick={handleSubmit} disabled={saving || !titre.trim() || previewCount === 0}
              data-testid="create-comptage-btn"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Création...' : `Créer${previewCount ? ` (${previewCount})` : ''}`}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Titre de la session *</label>
            <input value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex : Comptage nacelles — Juin 2026"
              data-testid="comptage-titre-input"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Date planifiée</label>
            <input type="date" value={datePlanifiee} onChange={e => setDatePlanifiee(e.target.value)}
              data-testid="comptage-date-input"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Filtres matériels</label>
            <div className="flex items-center gap-2">
              {previewCount !== null && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                  {previewCount} matériel{previewCount > 1 ? 's' : ''}
                </span>
              )}
              <button type="button" onClick={refreshPreview} disabled={loadingPreview}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all"
                style={{ color: 'var(--text-secondary)', borderColor: 'var(--border)' }}>
                <Filter size={11} />{loadingPreview ? '...' : 'Prévisualiser'}
              </button>
            </div>
          </div>

          {/* Filtre ancienneté du dernier comptage */}
          <div className="mb-3 p-3 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid var(--border-light)' }}>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              Dernier comptage remontant à plus de...
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ANCIENNETE_OPTS.map(opt => (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() => setAnciennete(opt.id)}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
                  style={{
                    background: anciennete === opt.id ? 'var(--primary-solid)' : 'white',
                    color: anciennete === opt.id ? 'white' : 'var(--text-secondary)',
                    borderColor: anciennete === opt.id ? 'var(--primary-solid)' : 'var(--border)',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTER_DEFS.map(def => def.type === 'multi' ? (
              <MultiSelectFilter key={def.key} filterKey={def.key} label={def.label} icon={def.icon}
                options={getOptions(def.key)} selected={sel[def.key] || []} onChange={v => setSel(s => ({ ...s, [def.key]: v }))}
                onSearch={['proprietaire_ids','exploitant_ids'].includes(def.key) ? onSearchEntreprises : undefined} />
            ) : (
              <TextFilter key={def.key} filterKey={def.key} label={def.label} icon={def.icon}
                value={sel[def.key]} onChange={v => setSel(s => ({ ...s, [def.key]: v }))} />
            ))}
          </div>
        </div>
      </div>
    </SidePanel>
  );
}

// ─── Note inline éditable (tous statuts) ─────────────────────────────────────
function NoteCell({ ligne, comptageId, isTerminee, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(ligne.commentaire || '');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.marquerStatutComptage(comptageId, ligne.materiel_id, {
        statut_comptage: ligne.statut_comptage,
        commentaire: draft.trim() || null,
      });
      onUpdate(r.data);
      setEditing(false);
    } catch { toast.error('Erreur mise à jour note'); }
    finally { setSaving(false); }
  };

  if (isTerminee) {
    return ligne.commentaire
      ? <span className="text-xs italic" style={{ color: '#B45309' }}>{ligne.commentaire}</span>
      : <span className="text-xs" style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  if (editing) {
    return (
      <div className="flex gap-1 min-w-[140px]">
        <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setDraft(ligne.commentaire || ''); } }}
          data-testid={`note-edit-${ligne.materiel_id}`}
          className="flex-1 px-2 py-1 text-xs rounded border outline-none min-w-0"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        <button onClick={save} disabled={saving} className="px-1.5 py-1 rounded text-xs font-bold text-white" style={{ background: 'var(--primary-solid)', flexShrink: 0 }}>✓</button>
        <button onClick={() => { setEditing(false); setDraft(ligne.commentaire || ''); }} className="px-1.5 py-1 rounded text-xs" style={{ background: '#F3F4F6', color: 'var(--text-muted)', flexShrink: 0 }}>✕</button>
      </div>
    );
  }

  return (
    <button onClick={() => { setDraft(ligne.commentaire || ''); setEditing(true); }}
      data-testid={`note-btn-${ligne.materiel_id}`}
      className="flex items-center gap-1 text-xs hover:underline cursor-text text-left"
      style={{ color: ligne.commentaire ? '#B45309' : 'var(--text-muted)' }}>
      <MessageSquare size={11} className="flex-shrink-0" style={{ opacity: ligne.commentaire ? 1 : 0.4 }} />
      <span className="truncate max-w-[120px]">{ligne.commentaire || 'Ajouter...'}</span>
    </button>
  );
}

// ─── Vue détail d'une session ─────────────────────────────────────────────────
function DetailView({ comptageId, onBack }) {
  const [comptage, setComptage] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterNote, setFilterNote]     = useState('');  // '' | 'avec' | 'sans'
  const [updating, setUpdating] = useState({});
  const [terminating, setTerminating] = useState(false);
  const [pendingNoteId, setPendingNoteId]     = useState(null);
  const [pendingNoteText, setPendingNoteText] = useState('');

  const load = useCallback(async () => {
    try { const res = await api.getComptage(comptageId); setComptage(res.data); }
    catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [comptageId]);

  useEffect(() => { load(); }, [load]);

  const isTerminee = comptage?.statut === 'terminee';

  const handleTerminer = async () => {
    const nb = comptage?.nb_en_attente || 0;
    const msg = nb > 0
      ? `${nb} matériel${nb > 1 ? 's' : ''} non comptés seront ignorés.\n\nConfirmer la clôture ?`
      : 'Confirmer la clôture de la session ?';
    if (!window.confirm(msg)) return;
    setTerminating(true);
    try {
      await api.terminerComptage(comptageId);
      setComptage(prev => ({ ...prev, statut: 'terminee' }));
      toast.success('Session clôturée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setTerminating(false); }
  };

  const marquer = async (materielId, statut, note = undefined) => {
    if (updating[materielId]) return;
    setUpdating(u => ({ ...u, [materielId]: true }));
    try {
      const r = await api.marquerStatutComptage(comptageId, materielId, {
        statut_comptage: statut,
        ...(note !== undefined ? { commentaire: note } : {}),
      });
      setComptage(prev => ({
        ...prev,
        nb_present: r.data.nb_present,
        nb_introuvable: r.data.nb_introuvable,
        nb_en_attente: r.data.nb_en_attente,
        lignes: prev.lignes.map(l => l.materiel_id === materielId ? r.data.ligne : l),
      }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setUpdating(u => ({ ...u, [materielId]: false })); }
  };

  const handleNoteUpdate = (data) => {
    setComptage(prev => ({
      ...prev,
      nb_present: data.nb_present,
      nb_introuvable: data.nb_introuvable,
      nb_en_attente: data.nb_en_attente,
      lignes: prev.lignes.map(l => l.materiel_id === data.ligne.materiel_id ? data.ligne : l),
    }));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 rounded-full"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!comptage) return null;

  const lignesFiltrees = (comptage.lignes || []).filter(l => {
    const s = search.toLowerCase();
    const matchSearch = !s ||
      (l.materiel_nom || '').toLowerCase().includes(s) ||
      (l.numero_parc || '').toLowerCase().includes(s) ||
      (l.numero_serie || '').toLowerCase().includes(s) ||
      (l.immatriculation || '').toLowerCase().includes(s);
    const matchStatut = !filterStatut || l.statut_comptage === filterStatut;
    const matchNote = !filterNote ||
      (filterNote === 'avec' && !!l.commentaire) ||
      (filterNote === 'sans' && !l.commentaire);
    return matchSearch && matchStatut && matchNote;
  });

  const pct = comptage.nb_total > 0
    ? Math.round(((comptage.nb_present + comptage.nb_introuvable) / comptage.nb_total) * 100)
    : 0;

  return (
    <div data-testid="comptage-detail-view">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => onBack(comptage)} data-testid="back-to-list-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}>
          <ChevronLeft size={16} /> Retour
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold" style={{  color: 'var(--text)' }}>{comptage.titre}</h2>
            {isTerminee
              ? <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #86EFAC' }}><Check size={11} /> Clôturée</span>
              : <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}><Clock size={11} /> En cours</span>}
            {comptage.plan_nom && (
              <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full" style={{ background: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE' }}>
                <CalendarClock size={10} /> {comptage.plan_nom}
              </span>
            )}
          </div>
          {comptage.date_planifiee && (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Planifié le {new Date(comptage.date_planifiee).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
        {!isTerminee && (
          <button onClick={handleTerminer} disabled={terminating} data-testid="terminer-session-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: '#16A34A' }}>
            <Check size={15} />{terminating ? 'Clôture...' : 'Terminer'}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="bg-white rounded-2xl p-5 border mb-5" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-6">
            {[
              { label: 'Présents',     val: comptage.nb_present,     color: '#16A34A' },
              { label: 'Introuvables', val: comptage.nb_introuvable, color: '#DC2626' },
              { label: 'En attente',   val: comptage.nb_en_attente,  color: '#6B7280' },
              { label: 'Total',        val: comptage.nb_total,       color: 'var(--text)' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-bold" style={{  color: s.color }}>{s.val}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
              </div>
            ))}
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold" style={{  color: 'var(--primary-solid)' }}>{pct}%</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Traités</p>
          </div>
        </div>
        <ProgressBar present={comptage.nb_present} introuvable={comptage.nb_introuvable} total={comptage.nb_total} />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex-1 relative" style={{ minWidth: '180px' }}>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher dans la liste..."
            data-testid="comptage-detail-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm outline-none"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
        </div>
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
          data-testid="comptage-detail-filter-statut"
          className="px-3 py-2.5 rounded-xl border text-sm outline-none"
          style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="present">Présent</option>
          <option value="introuvable">Introuvable</option>
        </select>
        <select value={filterNote} onChange={e => setFilterNote(e.target.value)}
          data-testid="comptage-detail-filter-note"
          className="px-3 py-2.5 rounded-xl border text-sm outline-none"
          style={{ borderColor: 'var(--border)', background: 'white', color: filterNote ? 'var(--primary-solid)' : 'var(--text)' }}>
          <option value="">Toutes les notes</option>
          <option value="avec">Avec note</option>
          <option value="sans">Sans note</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border)' }}>
                {['N° Parc', 'Matériel', 'N° Série', 'Immat.', 'Statut', 'Note', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignesFiltrees.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>Aucun matériel trouvé</td></tr>
              ) : lignesFiltrees.map((ligne, idx) => (
                <tr key={ligne.materiel_id} data-testid={`comptage-ligne-${ligne.materiel_id}`}
                  style={{ borderBottom: '1px solid var(--border-light)', background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    {ligne.numero_parc || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{ligne.materiel_nom}</p>
                    {(ligne.marque || ligne.modele) && (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{[ligne.marque, ligne.modele].filter(Boolean).join(' · ')}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{ligne.numero_serie || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{ligne.immatriculation || '—'}</td>
                  {/* Colonne Statut */}
                  <td className="px-4 py-3">
                    <StatutBadge statut={ligne.statut_comptage} />
                    {ligne.date_comptage && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {new Date(ligne.date_comptage).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </td>
                  {/* Colonne Note — éditable pour TOUS les statuts */}
                  <td className="px-4 py-3" style={{ minWidth: '140px' }}>
                    <NoteCell ligne={ligne} comptageId={comptageId} isTerminee={isTerminee} onUpdate={handleNoteUpdate} />
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-3 align-top">
                    {isTerminee ? (
                      <span className="text-xs" style={{ color: '#9CA3AF' }}>Clôturée</span>
                    ) : pendingNoteId === ligne.materiel_id ? (
                      <div className="flex flex-col gap-1.5 min-w-[200px]">
                        <input autoFocus type="text" value={pendingNoteText} onChange={e => setPendingNoteText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { marquer(ligne.materiel_id, 'introuvable', pendingNoteText.trim() || null); setPendingNoteId(null); setPendingNoteText(''); }
                            if (e.key === 'Escape') { setPendingNoteId(null); setPendingNoteText(''); }
                          }}
                          placeholder="Note (optionnelle)..." data-testid={`note-input-${ligne.materiel_id}`}
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none"
                          style={{ borderColor: '#FECACA', background: '#FEF2F2' }} />
                        <div className="flex gap-1.5">
                          <button onClick={() => { marquer(ligne.materiel_id, 'introuvable', pendingNoteText.trim() || null); setPendingNoteId(null); setPendingNoteText(''); }}
                            disabled={!!updating[ligne.materiel_id]} data-testid={`confirm-introuvable-${ligne.materiel_id}`}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: '#DC2626' }}>
                            <AlertCircle size={11} className="inline mr-1" />Confirmer
                          </button>
                          <button onClick={() => { setPendingNoteId(null); setPendingNoteText(''); }}
                            className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#F3F4F6', color: 'var(--text-muted)' }}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => marquer(ligne.materiel_id, ligne.statut_comptage === 'present' ? 'en_attente' : 'present')}
                          disabled={!!updating[ligne.materiel_id]} data-testid={`mark-present-${ligne.materiel_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                          style={{ background: ligne.statut_comptage === 'present' ? '#16A34A' : '#F0FDF4', color: ligne.statut_comptage === 'present' ? 'white' : '#16A34A', border: '1px solid #86EFAC' }}>
                          <CheckCircle2 size={13} /> Présent
                        </button>
                        <button onClick={() => ligne.statut_comptage === 'introuvable' ? marquer(ligne.materiel_id, 'en_attente') : (setPendingNoteId(ligne.materiel_id), setPendingNoteText(''))}
                          disabled={!!updating[ligne.materiel_id]} data-testid={`mark-introuvable-${ligne.materiel_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                          style={{ background: ligne.statut_comptage === 'introuvable' ? '#DC2626' : '#FEF2F2', color: ligne.statut_comptage === 'introuvable' ? 'white' : '#DC2626', border: '1px solid #FECACA' }}>
                          <AlertCircle size={13} /> Introuvable
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          {lignesFiltrees.length} matériel{lignesFiltrees.length !== 1 ? 's' : ''} affiché{lignesFiltrees.length !== 1 ? 's' : ''}
          {lignesFiltrees.length !== comptage.nb_total && ` sur ${comptage.nb_total}`}
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Sessions ──────────────────────────────────────────────────────────
function SessionsTab({ onSelectSession, onCreated }) {
  const { activeSociete, activeAgences } = useSociete();
  const [comptages, setComptages] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [searchList, setSearchList] = useState('');
  const [deleting, setDeleting]   = useState(null);

  // ── Filtres ────────────────────────────────────────────────────────────────
  const [filterStatut,       setFilterStatut]       = useState('');
  const [filterAnciennete,   setFilterAnciennete]   = useState('');
  const [filterSousFamille,  setFilterSousFamille]  = useState('');
  const [filterMarque,       setFilterMarque]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getComptages(); setComptages(res.data || []); }
    catch { toast.error('Erreur lors du chargement des sessions'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSociete?.societe_id, activeAgences.map(a => a.agence_id).join(',')]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (comptage) => {
    setShowCreate(false);
    setComptages(prev => [comptage, ...prev]);
    onCreated(comptage);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette session de comptage ?')) return;
    setDeleting(id);
    try {
      await api.deleteComptage(id);
      setComptages(prev => prev.filter(c => c.comptage_id !== id));
      toast.success('Session supprimée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur suppression'); }
    finally { setDeleting(null); }
  };

  // ── Options dérivées des sessions chargées ────────────────────────────────
  const sousFamillesOptions = (() => {
    const seen = new Map();
    comptages.forEach(c => {
      (c.filtres?.sous_famille_ids || []).forEach((id, i) => {
        if (!seen.has(id)) seen.set(id, c.filtres?.sous_famille_noms?.[i] || id);
      });
    });
    return [...seen.entries()].map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  })();

  const marquesOptions = (() => {
    const seen = new Map();
    comptages.forEach(c => {
      (c.filtres?.marque_ids || []).forEach((id, i) => {
        if (!seen.has(id)) seen.set(id, c.filtres?.marque_noms?.[i] || id);
      });
    });
    return [...seen.entries()].map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  })();

  // ── Filtrage ──────────────────────────────────────────────────────────────
  const filtered = comptages.filter(c => {
    if (searchList && !c.titre.toLowerCase().includes(searchList.toLowerCase())) return false;
    if (filterStatut === 'terminee' && c.statut !== 'terminee') return false;
    if (filterStatut === 'en_cours' && c.statut === 'terminee') return false;
    if (filterAnciennete) {
      const val = c.filtres?.dernier_comptage_anciennete;
      if (!val || String(val) !== filterAnciennete) return false;
    }
    if (filterSousFamille && !(c.filtres?.sous_famille_ids || []).includes(filterSousFamille)) return false;
    if (filterMarque && !(c.filtres?.marque_ids || []).includes(filterMarque)) return false;
    return true;
  });

  const hasActiveFilters = filterStatut || filterAnciennete || filterSousFamille || filterMarque;
  const selectCls = "px-3 py-2 rounded-xl border text-sm outline-none cursor-pointer";
  const selectStyle = { borderColor: 'var(--border)', background: 'white', color: 'var(--text)' };
  const selectActiveStyle = { ...selectStyle, borderColor: 'var(--primary-solid)', color: 'var(--primary-solid)' };

  return (
    <div>
      {showCreate && (
        <CreationPanel
          open={true}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Barre de recherche + bouton */}
      <div className="flex items-center justify-between mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input type="text" value={searchList} onChange={e => setSearchList(e.target.value)}
            placeholder="Rechercher une session..."
            data-testid="comptages-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm outline-none"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-comptage-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white ml-3"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouvelle session
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {/* Statut de session */}
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
          data-testid="sessions-filter-statut"
          className={selectCls} style={filterStatut ? selectActiveStyle : selectStyle}>
          <option value="">Tous les statuts</option>
          <option value="en_cours">En cours</option>
          <option value="terminee">Clôturées</option>
        </select>

        {/* Ancienneté dernier comptage */}
        <select value={filterAnciennete} onChange={e => setFilterAnciennete(e.target.value)}
          data-testid="sessions-filter-anciennete"
          className={selectCls} style={filterAnciennete ? selectActiveStyle : selectStyle}>
          <option value="">Dernier comptage : tous</option>
          <option value="1">&gt; 1 mois</option>
          <option value="3">&gt; 3 mois</option>
          <option value="6">&gt; 6 mois</option>
          <option value="12">&gt; 12 mois</option>
        </select>

        {/* Sous-famille */}
        {sousFamillesOptions.length > 0 && (
          <select value={filterSousFamille} onChange={e => setFilterSousFamille(e.target.value)}
            data-testid="sessions-filter-sous-famille"
            className={selectCls} style={filterSousFamille ? selectActiveStyle : selectStyle}>
            <option value="">Toutes sous-familles</option>
            {sousFamillesOptions.map(sf => <option key={sf.id} value={sf.id}>{sf.nom}</option>)}
          </select>
        )}

        {/* Marque */}
        {marquesOptions.length > 0 && (
          <select value={filterMarque} onChange={e => setFilterMarque(e.target.value)}
            data-testid="sessions-filter-marque"
            className={selectCls} style={filterMarque ? selectActiveStyle : selectStyle}>
            <option value="">Toutes les marques</option>
            {marquesOptions.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
          </select>
        )}

        {/* Jamais compté */}

        {/* Réinitialiser */}
        {hasActiveFilters && (
          <button onClick={() => { setFilterStatut(''); setFilterAnciennete(''); setFilterSousFamille(''); setFilterMarque(''); }}
            data-testid="sessions-filter-reset"
            className="flex items-center gap-1 px-3 py-2 rounded-xl border text-sm transition-colors hover:bg-red-50"
            style={{ borderColor: '#FECACA', color: '#DC2626' }}>
            <X size={13} /> Effacer
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border" style={{ borderColor: 'var(--border)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--primary-50)' }}>
            <ClipboardList size={28} style={{ color: 'var(--primary-solid)' }} />
          </div>
          <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)',  }}>
            {searchList ? 'Aucun résultat' : 'Aucune session de comptage'}
          </h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {searchList ? 'Essayez une autre recherche' : 'Lancez une session depuis un plan ou créez-en une manuellement'}
          </p>
          {!searchList && (
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={16} /> Nouvelle session
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="comptages-list">
          {filtered.map(comptage => {
            const pct = comptage.nb_total > 0 ? Math.round(((comptage.nb_present + comptage.nb_introuvable) / comptage.nb_total) * 100) : 0;
            return (
              <div key={comptage.comptage_id} onClick={() => onSelectSession(comptage.comptage_id)}
                data-testid={`comptage-card-${comptage.comptage_id}`}
                className="bg-white rounded-2xl border p-5 cursor-pointer hover:shadow-md transition-all group"
                style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-base" style={{  color: 'var(--text)' }}>{comptage.titre}</h3>
                      {comptage.statut === 'terminee'
                        ? <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #86EFAC' }}><Check size={10} /> Clôturée</span>
                        : <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}><Clock size={10} /> En cours</span>}
                      {comptage.plan_nom && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE' }}>
                          <CalendarClock size={10} /> {comptage.plan_nom}
                        </span>
                      )}
                      {comptage.date_planifiee && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                          <Calendar size={10} />{new Date(comptage.date_planifiee).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                      Créé le {new Date(comptage.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {comptage.created_by_nom && ` · par ${comptage.created_by_nom}`}
                    </p>
                    <div className="mb-3"><FilterChips filtres={comptage.filtres} /></div>
                    <ProgressBar present={comptage.nb_present} introuvable={comptage.nb_introuvable} total={comptage.nb_total} />
                    <div className="flex gap-4 mt-2 text-xs">
                      <span style={{ color: '#16A34A' }}>{comptage.nb_present} présents</span>
                      <span style={{ color: '#DC2626' }}>{comptage.nb_introuvable} introuvables</span>
                      <span style={{ color: '#6B7280' }}>{comptage.nb_en_attente} en attente</span>
                      <span className="ml-auto font-semibold" style={{ color: 'var(--primary-solid)' }}>{pct}% traité</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{  color: 'var(--text)' }}>{comptage.nb_total}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>matériels</p>
                    </div>
                    {comptage.statut !== 'terminee' && (
                      <button onClick={e => handleDelete(comptage.comptage_id, e)} disabled={deleting === comptage.comptage_id}
                        data-testid={`delete-comptage-${comptage.comptage_id}`}
                        className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50" style={{ color: '#DC2626' }}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function Comptages() {
  const { activeSociete } = useSociete();
  const [activeTab, setActiveTab]   = useState('plans');   // 'plans' | 'sessions'
  const [selectedId, setSelectedId] = useState(null);

  // Réinitialiser la vue détail quand la société change
  useEffect(() => {
    setSelectedId(null);
  }, [activeSociete?.societe_id]);

  if (selectedId) {
    return <DetailView comptageId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div data-testid="comptages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{  color: 'var(--text)' }}>Comptages</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Planification et gestion des inventaires physiques du parc matériel</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b mb-6" style={{ borderColor: 'var(--border)' }}>
        {[
          { id: 'plans',    label: 'Planifications', icon: CalendarClock },
          { id: 'sessions', label: 'Sessions',       icon: ClipboardList },
        ].map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className="flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all mr-1"
              style={{
                borderBottomColor: active ? 'var(--primary-solid)' : 'transparent',
                color: active ? 'var(--primary-solid)' : 'var(--text-muted)',
              }}>
              <Icon size={15} />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'plans'
        ? <PlansTab />
        : <SessionsTab
            onSelectSession={id => setSelectedId(id)}
            onCreated={comptage => { setActiveTab('sessions'); setSelectedId(comptage.comptage_id); }}
          />}
    </div>
  );
}
